import { getFileReviewApiClient } from './clientConfig';
import { normalizeFileReviewThread } from '../normalizeFileReviewThread';
import type {
  FileReviewAnchorKind,
  FileReviewThreadDto,
  PdfHighlightArea,
  SpreadsheetCellRange,
  WordTextRange
} from '../types';

function reviewBase(mountId: string): string {
  return `/api/file-review/${encodeURIComponent(mountId)}`;
}

async function reviewJson<T>(res: Response): Promise<T> {
  const { readJson, readErrorMessage } = getFileReviewApiClient();
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<T>(res);
}

export async function fetchFileReviewThread(
  mountId: string,
  path: string
): Promise<FileReviewThreadDto> {
  const { apiFetch } = getFileReviewApiClient();
  const params = new URLSearchParams({ path });
  const res = await apiFetch(`${reviewBase(mountId)}/thread?${params}`);
  return normalizeFileReviewThread(await reviewJson<unknown>(res));
}

export async function createFileReviewPdfAnchor(
  mountId: string,
  body: {
    path: string;
    pageIndex: number;
    areas: PdfHighlightArea[];
    selectedText: string;
  }
): Promise<{ id: string }> {
  const { apiFetch } = getFileReviewApiClient();
  const res = await apiFetch(`${reviewBase(mountId)}/anchors`, {
    method: 'POST',
    body: JSON.stringify({
      path: body.path,
      documentType: 'pdf',
      pageIndex: body.pageIndex,
      areas: body.areas,
      selectedText: body.selectedText
    })
  });
  return reviewJson<{ id: string }>(res);
}

export async function createFileReviewSpreadsheetAnchor(
  mountId: string,
  body: {
    path: string;
    sheetIndex: number;
    sheetName: string;
    ranges: SpreadsheetCellRange[];
    selectedText: string;
  }
): Promise<{ id: string }> {
  const { apiFetch } = getFileReviewApiClient();
  const res = await apiFetch(`${reviewBase(mountId)}/anchors`, {
    method: 'POST',
    body: JSON.stringify({
      path: body.path,
      documentType: 'spreadsheet',
      sheetIndex: body.sheetIndex,
      sheetName: body.sheetName,
      ranges: body.ranges,
      selectedText: body.selectedText
    })
  });
  return reviewJson<{ id: string }>(res);
}

export async function createFileReviewWordAnchor(
  mountId: string,
  body: {
    path: string;
    range: WordTextRange;
    selectedText: string;
  }
): Promise<{ id: string }> {
  const { apiFetch } = getFileReviewApiClient();
  const res = await apiFetch(`${reviewBase(mountId)}/anchors`, {
    method: 'POST',
    body: JSON.stringify({
      path: body.path,
      documentType: 'word',
      wordRange: body.range,
      selectedText: body.selectedText
    })
  });
  return reviewJson<{ id: string }>(res);
}

export async function createFileReviewMessage(
  mountId: string,
  body: {
    path: string;
    text: string;
    anchorId?: string | null;
    anchorKind?: FileReviewAnchorKind | null;
  }
): Promise<{ id: string }> {
  const { apiFetch } = getFileReviewApiClient();
  const res = await apiFetch(`${reviewBase(mountId)}/messages`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
  return reviewJson<{ id: string }>(res);
}

export async function updateFileReviewMessage(
  mountId: string,
  messageId: string,
  body: { text: string }
): Promise<void> {
  const { apiFetch, readErrorMessage } = getFileReviewApiClient();
  const res = await apiFetch(`${reviewBase(mountId)}/messages/${encodeURIComponent(messageId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
}

export async function deleteFileReviewMessage(mountId: string, messageId: string): Promise<void> {
  const { apiFetch, readErrorMessage } = getFileReviewApiClient();
  const res = await apiFetch(`${reviewBase(mountId)}/messages/${encodeURIComponent(messageId)}`, {
    method: 'DELETE'
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
}

export async function fetchMentionCandidates(
  mountId: string,
  q?: string,
  path?: string
): Promise<{ items: { displayName: string; notifyEmail: string; mentionToken: string; audience: string }[]; reason?: string | null }> {
  const { apiFetch } = getFileReviewApiClient();
  const params = new URLSearchParams();
  if (q?.trim()) params.set('q', q.trim());
  if (path?.trim()) params.set('path', path.trim());
  const qs = params.toString();
  const res = await apiFetch(
    `${reviewBase(mountId)}/mention-candidates${qs ? `?${qs}` : ''}`
  );
  return reviewJson(res);
}

export async function deleteFileReviewAnchor(
  mountId: string,
  anchorId: string,
  anchorKind: FileReviewAnchorKind
): Promise<void> {
  const { apiFetch, readErrorMessage } = getFileReviewApiClient();
  const params = new URLSearchParams({ kind: anchorKind });
  const res = await apiFetch(
    `${reviewBase(mountId)}/anchors/${encodeURIComponent(anchorId)}?${params}`,
    { method: 'DELETE' }
  );
  if (!res.ok) throw new Error(await readErrorMessage(res));
}
